/**
 * Telemetria remota + realtime via WebSocket próprio.
 *
 * Substitui o Realtime do Supabase. Assinaturas espelham o antigo módulo
 * de telemetria (referência histórica).
 */
import { apiGet, apiPost, getAccessToken, isCloudConfigured } from './client';
import type { TelemetrySample } from '../../store/Database';

export interface RemoteTelemetry {
  id: number;
  user_id: string;
  profile_key: string;
  freq: number | null;
  current_amp: number | null;
  dc_voltage: number | null;
  temp: number | null;
  load_pct: number | null;
  timestamp: string;
}

/** Envia uma amostra. Falhas são engolidas (nunca derrubam o loop de coleta). */
export async function pushTelemetry(sample: TelemetrySample): Promise<void> {
  if (!isCloudConfigured()) return;
  if (!getAccessToken()) return;

  try {
    await apiPost('/api/telemetry', {
      profileKey: sample.profileKey,
      frequency: sample.frequency,
      current: sample.current,
      dcVoltage: sample.dcVoltage,
      temperature: sample.temperature,
      load: sample.load,
      timestamp: sample.timestamp.toISOString(),
    });
  } catch (error) {
    console.warn('Erro ao enviar telemetria à API:', error instanceof Error ? error.message : error);
  }
}

export async function getRecentTelemetry(limit = 100): Promise<RemoteTelemetry[]> {
  if (!isCloudConfigured()) return [];
  if (!getAccessToken()) return [];

  try {
    return await apiGet<RemoteTelemetry[]>(`/api/telemetry/recent?limit=${limit}`);
  } catch (error) {
    console.warn('Erro ao buscar telemetria:', error instanceof Error ? error.message : error);
    return [];
  }
}

export type TelemetryCallback = (sample: RemoteTelemetry) => void;

/**
 * Assina o stream de telemetria em tempo real via WebSocket.
 *
 * Usa o access token como query param (navegador não envia headers em WS).
 * Reconecta sozinho com backoff, e refaz o token se expirar.
 * A assinatura ignora `userId` — o servidor sempre envia só os dados do
 * dono do token, então o isolamento não depende do cliente.
 */
export function subscribeTelemetry(callback: TelemetryCallback, _userId?: string): () => void {
  if (!isCloudConfigured()) return () => {};

  let socket: WebSocket | null = null;
  let closed = false;
  let attempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

  function connect(): void {
    if (closed) return;

    const token = getAccessToken();
    if (!token) {
      scheduleReconnect();
      return;
    }

    // Com proxy same-origin, apiUrl é vazio — deriva o host da própria página.
    // Com apiUrl explícita (http/https), converte para ws/wss.
    const wsBase = apiUrl
      ? apiUrl.replace(/^http/, 'ws')
      : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;
    try {
      socket = new WebSocket(`${wsBase}/ws/telemetry?token=${encodeURIComponent(token)}`);
    } catch {
      scheduleReconnect();
      return;
    }

    socket.onopen = () => {
      attempt = 0;
    };

    socket.onmessage = (event) => {
      try {
        const msg = JSON.parse(String(event.data)) as { type: string; data?: RemoteTelemetry };
        if (msg.type === 'telemetry.insert' && msg.data) {
          callback(msg.data);
        }
      } catch {
        // Mensagem malformada — ignorada.
      }
    };

    socket.onclose = () => {
      socket = null;
      if (!closed) scheduleReconnect();
    };

    socket.onerror = () => {
      // onclose dispara em seguida e cuida da reconexão.
    };
  }

  function scheduleReconnect(): void {
    if (closed) return;
    attempt++;
    const delay = Math.min(1000 * 2 ** Math.min(attempt, 5), 30_000);
    reconnectTimer = setTimeout(connect, delay);
  }

  connect();

  return () => {
    closed = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    if (socket) {
      socket.onclose = null;
      socket.close();
      socket = null;
    }
  };
}
